// useIncomingMessageSound.js — play a ding when new messages arrive from others

import { useEffect, useRef } from 'react';

import {
  isIncomingChannelMessage,
  playIncomingMessageSound,
} from 'channels/utils/incomingMessageSound';

/**
 * Plays a notification sound when the subscribed feed gains new messages
 * from someone other than the current user (including Ai).
 */
export default function useIncomingMessageSound({
  messages = [],
  currentUserKey = '',
  enabled = true,
  feedKey = '',
  loading = false,
} = {}) {
  const knownMessageKeysRef = useRef(new Set());
  const initializedRef = useRef(false);
  const activeFeedKeyRef = useRef('');

  useEffect(() => {
    const cleanFeedKey = String(feedKey || '').trim();

    if (cleanFeedKey !== activeFeedKeyRef.current) {
      activeFeedKeyRef.current = cleanFeedKey;
      knownMessageKeysRef.current = new Set();
      initializedRef.current = false;
    }
  }, [feedKey]);

  useEffect(() => {
    if (!enabled || !String(feedKey || '').trim()) return;

    if (loading) {
      initializedRef.current = false;
      return;
    }

    const rows = Array.isArray(messages) ? messages : [];
    const nextKeys = new Set(
      rows
        .map((row) => String(row?.key || '').trim())
        .filter(Boolean),
    );

    if (!initializedRef.current) {
      knownMessageKeysRef.current = nextKeys;
      initializedRef.current = true;
      return;
    }

    let shouldPlay = false;

    rows.forEach((row) => {
      const messageKey = String(row?.key || '').trim();
      if (!messageKey || knownMessageKeysRef.current.has(messageKey)) return;
      if (!isIncomingChannelMessage(row, currentUserKey)) return;
      shouldPlay = true;
    });

    knownMessageKeysRef.current = nextKeys;

    if (shouldPlay) {
      playIncomingMessageSound();
    }
  }, [currentUserKey, enabled, feedKey, loading, messages]);
}
